import React from 'react';
import { Download, FileText, Lock, Send, Table2 } from 'lucide-react';
import { Button } from '../../ui';
import { supplierSlug } from '../../../utils/supplierSlug';

interface FinalDeliverablesCardProps {
    isUnlocked: boolean;
    /** Deliverable id in flight: 'sourcing-summary' | 'smart-bom' | `supplier:<slug>`. */
    downloading: string | null;
    onDownload: (kind: 'sourcing-summary' | 'smart-bom') => void;
    /** Awarded supplier names, in award order — one shareable RFQ document each. */
    awardedSuppliers: string[];
    onDownloadSupplierRfq: (supplierName: string) => void;
}

const DELIVERABLES = [
    {
        kind: 'sourcing-summary' as const,
        icon: FileText,
        title: 'Sourcing Summary',
        description: 'My full sourcing trail — every quote compared, the split you confirmed, and why. Internal: it names prices suppliers have never seen.',
        cta: 'Download PDF',
    },
    {
        kind: 'smart-bom' as const,
        icon: Table2,
        title: 'Smart BOM',
        description: 'Excel workbook: awarded BOM with extended pricing, the full supplier pool, and the award summary.',
        cta: 'Download Excel',
    },
];

const FinalDeliverablesCard: React.FC<FinalDeliverablesCardProps> = ({
    isUnlocked,
    downloading,
    onDownload,
    awardedSuppliers,
    onDownloadSupplierRfq,
}) => (
    <div className="rounded-card border border-line bg-surface px-s6 py-s5">
        <div className="mb-s4 flex items-center justify-between gap-s4">
            <div>
                <h3 className="m-0 text-card-header text-ink">Final deliverables</h3>
                <p className="m-0 mt-[2px] text-body-sm text-ink-secondary">
                    {isUnlocked
                        ? 'Your award is locked in — download the final documents.'
                        : 'Confirm the award plan above to unlock your final documents.'}
                </p>
            </div>
            {!isUnlocked && (
                <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] border border-line bg-neutral-bg text-neutral-ink">
                    <Lock className="h-[13px] w-[13px]" />
                </span>
            )}
        </div>

        <div className="grid gap-s4 sm:grid-cols-2">
            {DELIVERABLES.map(({ kind, icon: Icon, title, description, cta }) => (
                <div
                    key={kind}
                    className={`rounded-[9px] border px-s5 py-s4 ${
                        isUnlocked ? 'border-line bg-surface hover:border-line-strong' : 'border-line bg-surface-hover opacity-60'
                    }`}
                >
                    <span className="mb-s3 flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-action-border bg-action-bg text-action">
                        <Icon className="h-4 w-4" />
                    </span>
                    <h4 className="m-0 text-row-title text-ink">{title}</h4>
                    <p className="m-0 mt-s1 min-h-[48px] text-body-sm text-ink-secondary">{description}</p>
                    <Button
                        fullWidth
                        className="mt-s3"
                        disabled={!isUnlocked || downloading !== null}
                        iconLeft={<Download />}
                        loading={downloading === kind}
                        loadingLabel="Preparing…"
                        onClick={() => onDownload(kind)}
                    >
                        {cta}
                    </Button>
                </div>
            ))}
        </div>

        {isUnlocked && awardedSuppliers.length > 0 && (
            <div className="mt-s5 border-t border-line pt-s4">
                <h4 className="m-0 text-row-title text-ink">Supplier RFQ documents</h4>
                <p className="m-0 mt-[2px] text-body-sm text-ink-secondary">
                    One per awarded supplier — attach it when you pick the conversation up with them directly. It recaps the terms they quoted, scoped to their award alone; no rival names or prices inside.
                </p>
                <ul className="m-0 mt-s3 flex list-none flex-col gap-s2 p-0">
                    {awardedSuppliers.map((name) => {
                        const busy = downloading === `supplier:${supplierSlug(name)}`;
                        return (
                            <li
                                key={name}
                                className="flex items-center justify-between gap-s4 rounded-[9px] border border-line bg-surface px-s4 py-s3 hover:border-line-strong"
                            >
                                <span className="flex min-w-0 items-center gap-s3">
                                    <span className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[7px] border border-action-border bg-action-bg text-action">
                                        <Send className="h-[13px] w-[13px]" />
                                    </span>
                                    <span className="truncate text-body-sm font-semibold text-ink">{name}</span>
                                </span>
                                <Button
                                    variant="secondary"
                                    disabled={downloading !== null}
                                    iconLeft={<Download />}
                                    loading={busy}
                                    loadingLabel="Preparing…"
                                    onClick={() => onDownloadSupplierRfq(name)}
                                >
                                    Download RFQ
                                </Button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        )}
    </div>
);

export default FinalDeliverablesCard;
